import { loadRegistry, type NodeTemplate } from '@features/flows/palette/registry';
import { useCanvasStore } from '@features/flows/state/canvas-store';
import { composeHandleId, parseHandleId } from '@features/flows/utils/edge';

export type EloraCommand = 'addNode' | 'removeNode' | 'addEdge' | 'removeEdge' | 'editNodeAttributes';

export type EloraEnvelope = {
  command: EloraCommand;
  args: Record<string, any>;
};

const KNOWN_COMMANDS = new Set<EloraCommand>(['addNode', 'removeNode', 'addEdge', 'removeEdge', 'editNodeAttributes']);
const EXEC_HINTS = new Set(['next', 'then', 'catch', 'finally', 'error', 'fail', 'success', 'done', 'trigger']);
const DATA_HINTS = new Set(['value', 'input', 'output', 'data', 'items', 'object', 'payload', 'params', 'config', 'body', 'result', 'response']);

let templatesPromise: Promise<Map<string, NodeTemplate>> | null = null;
const EDGE_RETRY_LIMIT = 10;
const EDGE_RETRY_DELAY_MS = 50;

export function extractCommandEnvelopes(payload: unknown, maxDepth = 6): EloraEnvelope[] {
  const out: EloraEnvelope[] = [];
  const seen = new Set<unknown>();

  const visit = (value: unknown, depth: number) => {
    if (depth > maxDepth || value == null) return;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return;
      if (trimmed[0] === '{' || trimmed[0] === '[') {
        try {
          const parsed = JSON.parse(trimmed);
          visit(parsed, depth + 1);
        } catch {
          // ignore invalid JSON strings
        }
      }
      return;
    }
    const type = typeof value;
    if (type !== 'object' && type !== 'function') return;
    if (seen.has(value)) return;
    seen.add(value);

    if (Array.isArray(value)) {
      for (const item of value) visit(item, depth + 1);
      return;
    }

    const v = value as Record<string, unknown>;
    const command = typeof v.command === 'string' ? (v.command as string) : null;
    if (command && KNOWN_COMMANDS.has(command as EloraCommand)) {
      const args = v.args;
      if (args && typeof args === 'object') {
        out.push({ command: command as EloraCommand, args: args as Record<string, any> });
      }
    }

    for (const child of Object.values(v)) visit(child, depth + 1);
  };

  try {
    if (typeof payload === 'string') {
      const parsed = JSON.parse(payload);
      visit(parsed, 0);
    } else {
      visit(payload, 0);
    }
  } catch {
    // ignore malformed JSON strings
  }

  return out;
}

async function ensureTemplates(): Promise<Map<string, NodeTemplate>> {
  const state = useCanvasStore.getState();
  if (state.templates && state.templates.size > 0) return state.templates;
  if (!templatesPromise) {
    templatesPromise = loadRegistry()
      .then((map) => {
        try {
          useCanvasStore.getState().setTemplates(map);
        } catch {}
        return map;
      })
      .finally(() => {
        templatesPromise = null;
      });
  }
  return templatesPromise!;
}

function resolveTemplateKey(rawNode: any): string | null {
  if (!rawNode) return null;
  const data = rawNode.data ?? {};
  const candidates = [
    data.templateKey,
    data.handler,
    rawNode.templateKey,
    rawNode.handler,
    rawNode.key,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length) {
      return candidate.trim();
    }
  }
  return null;
}

function extractRawHandle(handle: unknown, fallback?: string): string | undefined {
  if (typeof handle === 'string' && handle.trim().length) {
    const parsed = parseHandleId(handle);
    if (parsed) return parsed.raw;
    const parts = handle.split(':');
    const tail = parts[parts.length - 1]?.trim();
    if (tail && tail.length) return tail;
  }
  return fallback;
}

function inferChannel(
  sourceHandle: unknown,
  targetHandle: unknown,
  channelHint?: unknown,
  args?: Record<string, any>,
): 'data' | 'exec' {
  const hinted = typeof channelHint === 'string' ? channelHint.toLowerCase() : undefined;
  if (hinted === 'data' || hinted === 'exec') return hinted;

  const dataChannel = typeof args?.data?.channel === 'string' ? (args.data.channel as string).toLowerCase() : undefined;
  if (dataChannel === 'data' || dataChannel === 'exec') return dataChannel;

  const handles = [sourceHandle, targetHandle].filter((h): h is string => typeof h === 'string' && h.length > 0);
  if (handles.some((h) => h.startsWith('data:'))) return 'data';
  if (handles.some((h) => h.startsWith('exec:'))) return 'exec';

  const rawSource = extractRawHandle(sourceHandle);
  const rawTarget = extractRawHandle(targetHandle);
  const raws = [rawSource, rawTarget].filter((h): h is string => typeof h === 'string');

  if (raws.some((h) => DATA_HINTS.has(h.toLowerCase()))) return 'data';
  if (raws.some((h) => EXEC_HINTS.has(h.toLowerCase()))) return 'exec';

  return 'data';
}

function normalizeHandle(
  handle: unknown,
  channel: 'data' | 'exec',
  io: 'in' | 'out',
): { handle: string; raw: string } {
  const fallbackRaw = io === 'out' ? 'out' : 'in';
  const parsed = typeof handle === 'string' ? parseHandleId(handle) : null;
  const raw = parsed ? parsed.raw : extractRawHandle(handle, fallbackRaw) ?? fallbackRaw;
  if (parsed && parsed.channel === channel) {
    return { handle: handle as string, raw };
  }
  return { handle: composeHandleId(channel, io, raw), raw };
}

function stringOrNull(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim().length) return value.trim();
  return undefined;
}

function warnMissingTemplate(handler: string) {
  console.warn(`Elora command skipped: template "${handler}" not found.`);
}

type EdgeConnectParams = {
  id?: string;
  source: string;
  target: string;
  sourceHandle: string;
  targetHandle: string;
  data?: Record<string, any>;
};

function resolveNodeIdAgainstGraph(candidate: string | undefined, nodes: Array<{ id: string }>): string | undefined {
  if (!candidate) return undefined;
  const variants: string[] = [];
  const seen = new Set<string>();
  const addVariant = (id: string | undefined) => {
    if (!id) return;
    if (seen.has(id)) return;
    seen.add(id);
    variants.push(id);
  };
  addVariant(candidate);
  if (candidate.startsWith('node://')) {
    addVariant(candidate.slice('node://'.length));
  } else {
    addVariant(`node://${candidate}`);
  }
  for (const id of variants) {
    if (nodes.some((n) => n.id === id)) return id;
  }
  return undefined;
}

function connectEdgeWhenReady(params: EdgeConnectParams, attempt = 0) {
  const state = useCanvasStore.getState();
  const nodes = state.graph.nodes;
  const resolvedSource = resolveNodeIdAgainstGraph(params.source, nodes);
  const resolvedTarget = resolveNodeIdAgainstGraph(params.target, nodes);

  if (resolvedSource && resolvedTarget) {
    state.connectEdge({ ...params, source: resolvedSource, target: resolvedTarget } as any);
    return;
  }

  const missingSource = !resolvedSource;
  const missingTarget = !resolvedTarget;

  if (attempt >= EDGE_RETRY_LIMIT) {
    console.warn('Elora addEdge skipped: nodes missing for edge', {
      params,
      missingSource,
      missingTarget,
    });
    return;
  }

  const delay = Math.min(EDGE_RETRY_DELAY_MS * (2 ** attempt), 1000);
  setTimeout(() => connectEdgeWhenReady(params, attempt + 1), delay);
}

export async function dispatchEloraCommand(env: EloraEnvelope): Promise<void> {
  try {
    const store = useCanvasStore.getState();
    switch (env.command) {
      case 'addNode': {
        const rawNodeCandidate = env.args?.node ?? env.args;
        const rawNode = rawNodeCandidate && typeof rawNodeCandidate === 'object' ? rawNodeCandidate : null;
        if (!rawNode || typeof rawNode !== 'object') return;
        let handler = resolveTemplateKey(rawNode);
        await ensureTemplates();
        const templates = useCanvasStore.getState().templates ?? null;
        let templateMatch: NodeTemplate | undefined = handler && templates ? templates.get(handler) : undefined;
        const fallbackKeys = [
          handler,
          stringOrNull(rawNode.templateKey),
          stringOrNull(rawNode.handler),
          stringOrNull(rawNode.key),
          stringOrNull(env.args?.templateKey),
          stringOrNull(env.args?.handler),
          stringOrNull(env.args?.key),
          stringOrNull(rawNode.id),
          stringOrNull(env.args?.nodeId),
          stringOrNull(env.args?.id),
        ].filter((key): key is string => typeof key === 'string' && key.length > 0);
        if (templates) {
          for (const candidate of fallbackKeys) {
            const tpl = templates.get(candidate);
            if (tpl) {
              handler = candidate;
              templateMatch = tpl;
              break;
            }
          }
        }
        if (!templateMatch) {
          if (handler && templates) warnMissingTemplate(handler);
          else console.warn('Elora command skipped: template not resolved for addNode', { rawNode, args: env.args });
          return;
        }
        const resolvedHandler = templateMatch.key;
        const providedId =
          stringOrNull(rawNode.id) ?? stringOrNull(env.args?.nodeId) ?? stringOrNull(env.args?.id);
        const positionCandidate =
          (rawNode.position && typeof rawNode.position === 'object' ? rawNode.position : undefined) ??
          (env.args?.position && typeof env.args.position === 'object' ? env.args.position : undefined);
        const rawAttrs =
          (rawNode?.data?.attrs && typeof rawNode.data.attrs === 'object' ? rawNode.data.attrs : undefined) ??
          (env.args?.attrs && typeof env.args.attrs === 'object' ? env.args.attrs : undefined);
        let attrs = rawAttrs as Record<string, any> | undefined;
        if (!attrs && Array.isArray(rawNode.attributes)) {
          const converted: Record<string, any> = {};
          for (const item of rawNode.attributes) {
            const key = stringOrNull(item?.id) ?? stringOrNull(item?.key);
            if (!key) continue;
            if (Object.prototype.hasOwnProperty.call(item, 'value')) converted[key] = (item as any).value;
          }
          if (Object.keys(converted).length) attrs = converted;
        }
        const label =
          stringOrNull(rawNode.label) ??
          stringOrNull(rawNode?.data?.label) ??
          stringOrNull(env.args?.label);
        store.insertNodeFromCommand({
          id: providedId,
          handler: resolvedHandler,
          label,
          position: positionCandidate,
          attrs,
          persistedType: stringOrNull(rawNode.type),
        });
        console.info('[Elora] inserted node from command', {
          id: providedId,
          handler: resolvedHandler,
          label,
          attrsKeys: attrs ? Object.keys(attrs) : [],
        });
        return;
      }
      case 'removeNode': {
        const nodeId = stringOrNull(env.args?.nodeId) ?? stringOrNull(env.args?.id);
        if (nodeId) {
          const resolved = resolveNodeIdAgainstGraph(nodeId, store.graph.nodes);
          if (resolved) {
            store.removeNodeById(resolved);
            console.info('[Elora] removed node from command', { nodeId: resolved });
          } else console.warn('Elora removeNode skipped: node not found', { nodeId });
        }
        return;
      }
      case 'addEdge': {
        const args = env.args ?? {};
        const source = stringOrNull(args.source);
        const target = stringOrNull(args.target);
        if (!source || !target) return;
        const channel = inferChannel(args.sourceHandle, args.targetHandle, args.channel, args);
        const src = normalizeHandle(args.sourceHandle, channel, 'out');
        const tgt = normalizeHandle(args.targetHandle, channel, 'in');
        const data = typeof args.data === 'object' && args.data ? { ...args.data } : {};
        data.channel = channel;
        data.rawSourceHandle = src.raw;
        data.rawTargetHandle = tgt.raw;
        connectEdgeWhenReady({
          id: stringOrNull(args.id),
          source,
          target,
          sourceHandle: src.handle,
          targetHandle: tgt.handle,
          data,
        });
        return;
      }
      case 'removeEdge': {
        const args = env.args ?? {};
        const edgeId = stringOrNull(args.id);
        if (edgeId) {
          store.removeEdge(edgeId);
          return;
        }
        const sourceInput = stringOrNull(args.source);
        const targetInput = stringOrNull(args.target);
        if (!sourceInput || !targetInput) return;
        const nodes = store.graph.nodes;
        const resolvedSource = resolveNodeIdAgainstGraph(sourceInput, nodes);
        const resolvedTarget = resolveNodeIdAgainstGraph(targetInput, nodes);
        if (!resolvedSource || !resolvedTarget) return;
        const channel = inferChannel(args.sourceHandle, args.targetHandle, args.channel, args);
        const rawSrc = extractRawHandle(args.sourceHandle);
        const rawTgt = extractRawHandle(args.targetHandle);
        const edges = useCanvasStore.getState().graph.edges;
        const match = edges.find((edge) => {
          if (edge.source !== resolvedSource || edge.target !== resolvedTarget) return false;
          const edgeData = (edge.data ?? {}) as Record<string, any>;
          const edgeChannel =
            (typeof edgeData.channel === 'string' ? edgeData.channel : undefined) ??
            (String(edge.sourceHandle ?? '').startsWith('data:') || String(edge.targetHandle ?? '').startsWith('data:')
              ? 'data'
              : 'exec');
          if (edgeChannel !== channel) return false;
          const edgeRawSrc = edgeData.rawSourceHandle ?? extractRawHandle(edge.sourceHandle);
          const edgeRawTgt = edgeData.rawTargetHandle ?? extractRawHandle(edge.targetHandle);
          if (rawSrc && edgeRawSrc && rawSrc !== edgeRawSrc) return false;
          if (rawTgt && edgeRawTgt && rawTgt !== edgeRawTgt) return false;
          return true;
        });
        if (match) store.removeEdge(match.id);
        return;
      }
      case 'editNodeAttributes': {
        const nodeId = stringOrNull(env.args?.nodeId) ?? stringOrNull(env.args?.id);
        const patch = env.args?.attrs ?? env.args?.patch;
        if (!nodeId || !patch || typeof patch !== 'object') return;
        store.patchNodeAttrs(nodeId, patch as Record<string, any>);
        return;
      }
      default:
        return;
    }
  } catch (err) {
    console.warn('Failed to dispatch Elora command envelope', env, err);
  }
}
